import { msg } from '@lingui/core/macro';
import styled from '@emotion/styled';
import { useLingui } from '@lingui/react/macro';
import { Ban, Check, Pencil, X } from 'lucide-react';
import { StepperMainTool } from 'modules/main/domain/utils/stepper-tool';
import { ApplicationStatus } from 'modules/teams/domain/enums/application-status';
import { getApplicationLimitMessage, isApplicationLimitExceeded } from 'modules/teams/domain/utils/application-limit-error';
import { toastApplicationSaveError } from 'modules/teams/ui/utils';
import {
  championshipPlayerApplicationChangeNumber,
  championshipPlayerApplicationChangePosition,
  upsertChampionshipPlayerApplication,
} from 'modules/teams/store/championship-teams/actions';
import { PlayerNumberInput } from 'modules/teams/ui/components/styles';
import { fetchChampionshipTeams } from 'modules/championship/store/championship/actions';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { OnChangeValue } from 'react-select';
import { toast } from 'react-toastify';
import { trackEvent } from 'shared/analytics';
import { USER_DEFAULT_AVATAR } from 'shared/constants/links';
import { getBirthDay } from 'shared/utils';
import { AppDispatch } from 'store/store';
import { UDCheckbox } from 'ud-form/components';
import { SelectOption } from 'ud-form/components/select/component';
import UDIconButton from 'ud-ui/components/icon-button';
import UDText from 'ud-ui/components/text';
import { PlayerPosition } from 'modules/teams/domain/enums/player-position';
import { PlayerApplication } from 'modules/teams/domain/interfaces/player-application';
import PositionSelect from '../position-select';
import { TeamPlayerAvatar } from '../team-card/styles';
import { theme } from 'styles/theme/theme';

const CardContainer = styled.div<{ isSelected?: boolean }>`
  display: flex;
  align-items: center;
  padding: 0 20px 0 0;
  height: 72px;
  background-color: ${({ isSelected }) => (isSelected ? '#D9B24480' : theme.colors.BorderGray50)};
  border-radius: 8px;
  margin-bottom: 10px;
  user-select: none;
  transition: background-color 0.2s ease;

  &:hover:not(:has(button:hover, input:hover, select:hover, [role='button']:hover, .checkbox-area:hover, .interactive:hover)) {
    background-color: ${theme.colors.AccentCornsilk};
    cursor: pointer;
    .avatar-wrapper {
      &:after {
        opacity: 1;
      }

      svg {
        opacity: 1;
      }
    }
  }
`;

const AvatarWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: #fff3d18f;
    opacity: 0;
    transition: opacity 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  svg {
    position: absolute;
    z-index: 1;
    opacity: 0;
    transition: opacity 0.2s ease;
  }
`;

interface TeamPlayerCardProps {
  teamId: number;
  index: number;
  player: PlayerApplication;
  isUpdating?: boolean;
  isSelected?: boolean;
  onSelect?: (id: number) => void;
  onMouseDownCheckbox?: (event: React.MouseEvent<HTMLDivElement>) => void;
  onMouseEnterCheckbox?: () => void;
  onMouseLeaveCheckbox?: () => void;
  onClick?: () => void;
  // passed from top-level to avoid duplicate store lookups
  applicationMaxPlayers?: number | null;
  applications?: PlayerApplication[];
}

const TeamPlayerCard = ({
  player,
  isUpdating,
  isSelected,
  onSelect,
  onMouseDownCheckbox,
  onMouseEnterCheckbox,
  onMouseLeaveCheckbox,
  onClick,
  applicationMaxPlayers,
  applications,
}: TeamPlayerCardProps) => {
  const { t } = useLingui();
  const dispatch = useDispatch<AppDispatch>();

  // Используем useRef для хранения таймера
  const timerRef = React.useRef<NodeJS.Timeout | null>(null);

  const [data, setData] = useState(player);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const lastSubmittedNumber = React.useRef(player.number);
  const lastSubmittedPosition = React.useRef(player.position);

  useEffect(() => {
    setData(player);
    lastSubmittedNumber.current = player.number;
    lastSubmittedPosition.current = player.position;
  }, [player]);

  const combinedUpdating = isUpdating || isSubmitting;

  const handleChange = useCallback(
    (nextState: ApplicationStatus) => {
      // Pre-validate max allowed accepted players before accepting a player
      if (nextState === ApplicationStatus.ACCEPTED) {
        const max = applicationMaxPlayers ?? null;
        if (typeof max === 'number') {
          const apps = applications || [];
          const nextAcceptedCount = (apps as PlayerApplication[]).filter((app: PlayerApplication) => {
            return app.status === ApplicationStatus.ACCEPTED;
          }).length;

          if (nextAcceptedCount >= max) {
            toast.error(t`Заявляемое кол-во игроков не соответствует заявочной кампании. Сверьтесь с условиями турнира.`);
            return;
          }
        }
      }

      setIsSubmitting(true);
      return dispatch(
        upsertChampionshipPlayerApplication({
          id: data.id,
          teamId: data.teamId,
          championshipId: data.championshipId,
          createUserOrUpdateApplication: {
            userId: data.player.id,
            name: data.player.firstName,
            surname: data.player.lastName,
            middleName: data.player.middleName,
            birthDate: data.player.birthDate,
            position: data.position ? data.position.toUpperCase() : data.position,
            number: data.number,
            state: nextState,
          },
        }),
      )
        .unwrap()
        .then((application) => {
          StepperMainTool.setByPath('stepper:main:applicationTeam', true);
          trackEvent('Team Application Submitted', {
            championship_id: data.championshipId,
            team_id: data.teamId,
            flow: 'v1',
            source: 'player-card',
          });
          if (application.status === 'ACCEPTED') {
            // `data.status` — статус до мутации: событие только на реальную смену,
            // а не на повторное принятие уже заявленного игрока.
            if (data.status !== ApplicationStatus.ACCEPTED) {
              trackEvent('Player Application Accepted', {
                championship_id: data.championshipId,
                team_id: data.teamId,
                flow: 'v1',
                source: 'player-card',
                players_count: 1,
              });
            }
            toast.success(t`Игрок успешно заявлен`);
          } else if (application.status === 'DECLINED') {
            toast.success(t`Заявка игрока успешно отклонена`);
          } else {
            toast.success(t`Игрок возвращён в незаявленные`);
          }

          dispatch(fetchChampionshipTeams(data.championshipId));
        })
        .catch((error: unknown) => {
          if (isApplicationLimitExceeded(error)) {
            toast.error(getApplicationLimitMessage(applicationMaxPlayers, 'player'));
          } else toastApplicationSaveError(error, msg`Не удалось изменить статус заявки игрока`);
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    },
    [dispatch, data, applicationMaxPlayers, applications, t],
  );

  const name = [data.player.lastName, data.player.firstName, data.player.middleName].filter(Boolean).join(' ');

  const birthDate = data.player.birthDate ? new Date(data.player.birthDate) : null;
  const full = getBirthDay({ birthDate: birthDate ?? undefined });

  const handleNumberChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const number = parseInt(event.target.value);
      const val = isNaN(number) ? 0 : number < 0 ? 0 : number;

      setData({
        ...data,
        number: val,
      });

      // Реализуем debounce напрямую
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(() => {
        if (val === lastSubmittedNumber.current || isSubmitting) return;

        setIsSubmitting(true);
        dispatch(
          championshipPlayerApplicationChangeNumber({
            id: data.id,
            teamId: data.teamId,
            championshipId: data.championshipId,
            createUserOrUpdateApplication: {
              userId: data.player.id,
              name: data.player.firstName,
              surname: data.player.lastName,
              middleName: data.player.middleName,
              birthDate: data.player.birthDate,
              position: data.position ? data.position.toUpperCase() : data.position,
              number: val,
              state: data.status.toUpperCase() as ApplicationStatus,
            },
          }),
        )
          .unwrap()
          .finally(() => {
            setIsSubmitting(false);
          });
      }, 2 * 1000);
    },
    [dispatch, data, isSubmitting],
  );

  // Добавляем обработчик потери фокуса
  const handleNumberBlur = useCallback(() => {
    // Очищаем существующий таймер debounce, чтобы избежать дублирования запросов
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (data.number === lastSubmittedNumber.current || isSubmitting) {
      return;
    }

    // Немедленно отправляем текущее значение
    setIsSubmitting(true);
    dispatch(
      championshipPlayerApplicationChangeNumber({
        id: data.id,
        teamId: data.teamId,
        championshipId: data.championshipId,
        createUserOrUpdateApplication: {
          userId: data.player.id,
          name: data.player.firstName,
          surname: data.player.lastName,
          middleName: data.player.middleName,
          birthDate: data.player.birthDate,
          position: data.position ? data.position.toUpperCase() : data.position,
          number: data.number,
          state: data.status.toUpperCase() as ApplicationStatus,
        },
      }),
    )
      .unwrap()
      .finally(() => {
        setIsSubmitting(false);
      });
  }, [dispatch, data, isSubmitting]);

  const handlePositionChange = useCallback(
    (option: OnChangeValue<SelectOption, false>) => {
      const position = option ? (option.value as PlayerPosition) : undefined;

      if (position === lastSubmittedPosition.current || isSubmitting) {
        return;
      }

      setData({
        ...data,
        position,
      });

      setIsSubmitting(true);
      dispatch(
        championshipPlayerApplicationChangePosition({
          id: data.id,
          teamId: data.teamId,
          championshipId: data.championshipId,
          createUserOrUpdateApplication: {
            userId: data.player.id,
            name: data.player.firstName,
            surname: data.player.lastName,
            middleName: data.player.middleName,
            birthDate: data.player.birthDate,
            position: position ? position.toUpperCase() : position,
            number: data.number,
            state: data.status.toUpperCase() as ApplicationStatus,
          },
        }),
      )
        .unwrap()
        .finally(() => {
          setIsSubmitting(false);
        });
    },
    [dispatch, data, isSubmitting],
  );

  const canAccept = player.status !== ApplicationStatus.ACCEPTED;
  const canDecline = player.status !== ApplicationStatus.DECLINED;
  const canCancel = player.status === ApplicationStatus.DECLINED;

  const handleContainerClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement;
      const isInteractive = target.closest('button, input, select, [role="button"], .interactive, .checkbox-area');

      if (!isInteractive) {
        onClick?.();
      }
    },
    [onClick],
  );

  return (
    <CardContainer isSelected={isSelected} onClick={handleContainerClick}>
      <div
        className="flex-center checkbox-area"
        style={{ width: 60, cursor: 'pointer', minWidth: 60 }}
        onMouseDown={onMouseDownCheckbox}
        onMouseEnter={onMouseEnterCheckbox}
        onMouseLeave={onMouseLeaveCheckbox}
      >
        <UDCheckbox checked={isSelected} onClick={() => onSelect?.(data.player.id)} />
      </div>
      <AvatarWrapper className="avatar-wrapper">
        <TeamPlayerAvatar url={data.player.logoUrl || USER_DEFAULT_AVATAR} size={60} minSize={60} square />
        <Pencil size={20} color="#000000" />
      </AvatarWrapper>
      <UDText
        type="subhead"
        className="bold ml-5"
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: '40%',
          maxWidth: 300,
        }}
      >
        {name}
      </UDText>
      <UDText
        type="subhead"
        style={{
          flexBasis: '15%',
          whiteSpace: 'nowrap',
          width: 150,
          marginLeft: 20,
          paddingRight: 20,
        }}
      >
        {full}
      </UDText>

      <div className="flex items-center gap-5" style={{ marginLeft: 'auto', marginRight: 20 }}>
        <PlayerNumberInput
          wrapperProps={{
            style: {
              minWidth: 127,
            },
          }}
          name="number"
          placeholder={t`Номер игрока`}
          min={0}
          value={data.number}
          onChange={handleNumberChange}
          onBlur={handleNumberBlur}
          disabled={combinedUpdating}
        />
        <PositionSelect
          className="interactive"
          name="position"
          value={data.position}
          placeholder={t`Амплуа`}
          onChange={handlePositionChange}
          isDisabled={combinedUpdating}
        />
        <div className="flex items-center justify-end" style={{ width: 92, minWidth: 92, gap: 20 }}>
          {canAccept && (
            <UDIconButton
              icon={<Check />}
              variant="success"
              tooltip={t`Заявить игрока`}
              disabled={combinedUpdating}
              onClick={() => handleChange(ApplicationStatus.ACCEPTED)}
            />
          )}
          {canDecline && (
            <UDIconButton
              icon={<Ban />}
              variant="danger"
              tooltip={t`Отклонить`}
              disabled={combinedUpdating}
              onClick={() => handleChange(ApplicationStatus.DECLINED)}
            />
          )}
          {canCancel && (
            <UDIconButton
              icon={<X />}
              variant="default"
              tooltip={t`Вернуть в незаявленные`}
              disabled={combinedUpdating}
              onClick={() => handleChange(ApplicationStatus.PENDING)}
            />
          )}
        </div>
      </div>
    </CardContainer>
  );
};

export default TeamPlayerCard;
